import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useToast } from "@netfront/ekardo-content-library";
import { Button, Input, Select, Textarea } from '@netfront/ui-library';
import { ISubscribeFormFields } from './SubscribeForm.interfaces';
import { addUserToEmailSubscription } from './addUserToEmailSubscription';

// Source - https://stackoverflow.com/a/53210158
const PHONE_NUMBER_REGEX = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
const mapStringToUndefinedIfFalsy = (value: string) => (value ? value : undefined);

export const SubscribeForm = () => {
  const STATE_OPTIONS = [
    { id: 'NSW', name: 'NSW', value: 'NSW' },
    { id: 'ACT', name: 'ACT', value: 'ACT' },
    { id: 'NT', name: 'NT', value: 'NT' },
    { id: 'QLD', name: 'QLD', value: 'QLD' },
    { id: 'SA', name: 'SA', value: 'SA' },
    { id: 'TAS', name: 'TAS', value: 'TAS' },
    { id: 'VIC', name: 'VIC', value: 'VIC' },
    { id: 'WA', name: 'WA', value: 'WA' },
  ];

  const REFERER_OPTIONS = [
    { id: '1', name: 'Email from the research team', value: 'Email from the research team' },
    { id: '2', name: 'Email forwarded by school leadership', value: 'Email forwarded by school leadership' },
    { id: '3', name: 'School principal network communications', value: 'School principal network communications' },
    { id: '4', name: 'NSW Department of Education communication channels', value: 'NSW Department of Education communication channels' },
    {
      id: '5',
      name: 'Professional association newsletter (e.g., PDHPE, wellbeing networks)',
      value: 'Professional association newsletter (e.g., PDHPE, wellbeing networks)',
    },
    { id: '6', name: 'School wellbeing team / counselling staff', value: 'School wellbeing team / counselling staff' },
    { id: '7', name: 'Colleague recommendation (word of mouth)', value: 'Colleague recommendation (word of mouth)' },
    { id: '8', name: 'University or research institute website', value: 'University or research institute website' },
    { id: '9', name: 'Social media (e.g., LinkedIn, Facebook)', value: 'Social media (e.g., LinkedIn, Facebook)' },
    { id: '10', name: 'Online advertisement', value: 'Online advertisement' },
    {
      id: '11',
      name: 'Previous involvement in Health4Life or related projects',
      value: 'Previous involvement in Health4Life or related projects',
    },
    { id: '12', name: 'Other', value: 'Other' },
  ];

  const { handleToastSuccess } = useToast();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const formik = useFormik<ISubscribeFormFields>({
    initialValues: {
      email: '',
      name: '',
      mobile: undefined,
      location: STATE_OPTIONS[0].value,
      organisation: '',
      referrer: '',
      otherReferrer: '',
    },
    validationSchema: yup.object().shape({
      comments: yup.string(),
      email: yup.string().required('Please enter a valid email'),
      name: yup.string().required('Please enter your name'),
      mobile: yup.string().notRequired().transform(mapStringToUndefinedIfFalsy).matches(PHONE_NUMBER_REGEX, 'Phone number is not valid'),
      location: yup.string().required('Please enter your state'),
      organisation: yup.string(),
      referrer: yup.string().required('Please select a referrer'),
      otherReferrer: yup.string().when('referrer', {
        is: 'Other',
        then: (schema) => schema.required('Please specify how you heard about this study'),
      }),
    }),
    onSubmit: async ({ mobile, comments, email, name, location, referrer, organisation, otherReferrer }: ISubscribeFormFields, actions) => {
      const finalReferrer = referrer === 'Other' ? otherReferrer : referrer;

      addUserToEmailSubscription({
        emailAddress: email,
        listId: String(process.env.REACT_APP_CREATE_SEND_LIST_ID),
        name,
        customFields: {
          PhoneNumber: String(mobile),
          Comments: String(comments),
          State: location,
          Organisation: String(organisation),
          Referrer: String(finalReferrer),
          IsExpressionOfInterestForPhase3: String(true),
        },
        onSuccess: () => {
          actions.resetForm();

          handleToastSuccess({
            message: `Successfully subscribed: ${email}`,
          });

          setIsSubmitting(false);
        },
      });
    },
  });

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  return (
    <form
      onSubmit={(e) => {
        setIsSubmitting(true);
        handleSubmit(e);
      }}
    >
      <Input
        errorMessage={touched.name && errors.name ? errors.name : ''}
        id="name"
        labelText="Name"
        placeholder="Enter name"
        name="name"
        value={values.name}
        onChange={handleChange}
        type="text"
        isRequired
      />

      <Input
        errorMessage={touched.email && errors.email ? errors.email : ''}
        id="email"
        labelText="Email"
        placeholder="Enter email"
        name="email"
        value={values.email}
        onChange={handleChange}
        type="email"
        isRequired
      />

      <Input
        errorMessage={touched.mobile && errors.mobile ? errors.mobile : ''}
        id="mobile"
        labelText="Phone Number"
        placeholder="Phone number (optional)"
        name="mobile"
        value={values.mobile}
        onChange={handleChange}
        type="tel"
      />

      <Select
        id="location"
        labelText="Location (state)"
        name="location"
        options={STATE_OPTIONS}
        value={values.location}
        onChange={handleChange}
        isRequired
      />

      <Input
        errorMessage={touched.organisation && errors.organisation ? errors.organisation : ''}
        id="organisation"
        labelText="Organisation/school"
        placeholder="Enter organisation"
        name="organisation"
        value={values.organisation}
        onChange={handleChange}
        type="text"
      />

      <Select
        errorMessage={touched.referrer && errors.referrer ? errors.referrer : ''}
        id="referrer"
        labelText="How did you find out about this study?"
        name="referrer"
        options={REFERER_OPTIONS}
        value={values.referrer}
        onChange={handleChange}
        isRequired
      />

      {values.referrer === 'Other' && (
        <Input
          errorMessage={touched.otherReferrer && errors.otherReferrer ? errors.otherReferrer : ''}
          id="otherReferrer"
          labelText="Please specify"
          placeholder="Enter details"
          name="otherReferrer"
          value={values.otherReferrer}
          onChange={handleChange}
          type="text"
        />
      )}

      <Textarea
        errorMessage={touched.comments && errors.comments ? errors.comments : ''}
        id="comments"
        labelText="Any additional comments?"
        placeholder="Enter comments"
        name="comments"
        value={values.comments}
        onChange={handleChange}
      />

      <div className="flex justify-center pt-4">
        <Button additionalClassNames="w-full" text="Express Interest" type="submit" isLoading={isSubmitting} variant="tertiary" />
      </div>
    </form>
  );
};
